/**
 * How the product writes dates, times and names.
 *
 * These are small enough to look like they belong beside the components that
 * use them, which is exactly how the two clients would end up showing the same
 * timestamp in two different shapes. They are one product, so they read the
 * clock the same way.
 *
 * `Intl` is available in every browser the client supports and in Hermes with
 * `intl` enabled, which React Native ships by default.
 */
const RU = 'ru';

/** Time of day, for a message that is already dated by its group heading. */
export function formatTimeOfDay(iso: string): string {
  return new Intl.DateTimeFormat(RU, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/**
 * When a conversation last moved, as a chat list shows it: a time today, a
 * date this year, a short date before that.
 */
export function formatChatTimestamp(iso: string, now: Date = new Date()): string {
  const value = new Date(iso);
  if (value.toDateString() === now.toDateString()) return formatTimeOfDay(iso);
  const sameYear = value.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(RU, {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: '2-digit' }),
  }).format(value);
}

/** The heading that separates one day of a conversation from the next. */
export function formatDayHeading(iso: string, now: Date = new Date()): string {
  const value = new Date(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (value.toDateString() === now.toDateString()) return 'Сегодня';
  if (value.toDateString() === yesterday.toDateString()) return 'Вчера';
  return new Intl.DateTimeFormat(RU, {
    day: 'numeric',
    month: 'long',
    ...(value.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(value);
}

/** A precise moment, for a device list where "вчера" is not good enough. */
export function formatExactMoment(iso: string): string {
  return new Intl.DateTimeFormat(RU, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Two letters, from the first two words when there are any. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase() || '??';
}
